import type {Lead} from 'types/entities/Lead'
import {LeadSource} from 'types/enums/LeadSource'
import {getLeadFormAddressFields} from 'utils/leads/leadEventFormDefaults'
import {getLeadCompanyRegion} from 'utils/companiesUtils'
import {getCantonsByCountry} from 'utils/cantonUtils'
import {normalizeAddressSpelling, searchGoogleAddresses} from './googleAddressService'

// Session-only cache: share lookups across pages and detail, without storing Google data locally.
const lookups = new Map<string, Promise<string>>()
const normalize = (value: string) => normalizeAddressSpelling(value).trim().toLocaleLowerCase()

export interface CantonEnrichmentOptions {
    persist?: (lead: Lead, region: string) => Promise<Lead | null>
    onEnriched?: (lead: Lead, original: Lead) => void
}

export const enrichOpportunityCanton = async (lead: Lead, options: CantonEnrichmentOptions = {}): Promise<Lead> => {
    if (lead.source !== LeadSource.EventForm || getLeadCompanyRegion(lead)) return lead

    const address = getLeadFormAddressFields(lead.company)
    if (address.state?.trim() || !address.street_address?.trim() ||
        !address.postal_code?.trim() || !address.city?.trim()) return lead

    const country = normalize(address.country || '')
    if (!['ch', 'switzerland', 'schweiz', 'suisse', 'svizzera'].includes(country)) return lead

    const query = `${address.street_address}, ${address.postal_code} ${address.city}, Switzerland`
    let pending = lookups.get(normalize(query))
    if (!pending) {
        const lookup = (async () => {
            try {
                const suggestions = await searchGoogleAddresses(query, 'CH')
                // Never infer a canton from suggestion text or take an arbitrary first result.
                const candidates = suggestions.filter(item =>
                    item.resolvedAddress?.country_code === 'CH' &&
                    item.resolvedAddress.postcode === address.postal_code.trim() &&
                    normalize(item.resolvedAddress.city) === normalize(address.city))
                // Every matching candidate must have an explicit canton component.
                const matches = candidates.map(item => item.geocoderResult?.address_components.find(
                    component => component.types.includes('administrative_area_level_1'))?.short_name)
                if (!matches.length) return ''
                const regions = new Set(matches)
                if (regions.size !== 1) return ''
                const region = matches[0]
                return getCantonsByCountry('CH').some(option =>
                    option.country === 'CH' && option.value === region && region !== 'Sonstige') ? region : ''
            } catch {
                return ''
            }
        })()
        // Cache the bounded request itself so a hung API request can be retried.
        let timer: ReturnType<typeof setTimeout> | undefined
        pending = Promise.race([
            lookup,
            new Promise<string>(resolve => { timer = setTimeout(() => resolve(''), 4000) }),
        ]).finally(() => clearTimeout(timer))
        if (lookups.size >= 100) lookups.delete(lookups.keys().next().value!)
        lookups.set(normalize(query), pending)
        // Failed lookups can be retried on a later load.
        const cachedRequest = pending
        void pending.then(region => {
            if (!region && lookups.get(normalize(query)) === cachedRequest) lookups.delete(normalize(query))
        })
    }

    const region = await pending
    if (region && options.persist) {
        try {
            return await options.persist(lead, region) ?? lead
        } catch {
            // A failed save must not be presented as persisted data.
            return lead
        }
    }
    return region ? {...lead, state: region} : lead
}

export const enrichOpportunityCantons = async (leads: Lead[], options: CantonEnrichmentOptions = {}): Promise<Lead[]> => {
    const result = [...leads]
    let next = 0
    await Promise.all(Array.from({length: Math.min(3, leads.length)}, async () => {
        while (next < leads.length) {
            const index = next++
            result[index] = await enrichOpportunityCanton(leads[index], options)
            if (result[index] !== leads[index]) options.onEnriched?.(result[index], leads[index])
        }
    }))
    return result
}
